import React, { useCallback, useEffect, useState } from 'react';

import {
  Botao,
  EmptyState,
  ErrorState,
  ListaSelecao,
  Modal,
  Pagination,
  SearchInput,
  Skeleton,
} from '../../../ui';

import { fetchSgaMarcas } from '../../../api/sga-marca/sga-marcaApi';
import { BikeMarca, listarBikeMarcas } from '../../../api/bike-eletrica/bikeEletricaApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha da marca do veículo (reescrito na at-59 sobre o `Modal` do kit, a pedido
 * de Victor — mesmo caminho do `ModalRegionaisUsuario` na at-58).
 *
 * ⚠️ **É componente de DOMÍNIO**, e por isso mora em `components/`: conhece o
 * espelho de marcas do SGA e o catálogo de motocicletas elétricas. O que ele
 * reaproveita do kit — `Modal`, `ListaSelecao`, `SearchInput`, `Pagination`,
 * `Skeleton`, `EmptyState`, `ErrorState` — é puro.
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system** (era `.modal-overlay`/`.modal-content`, com
 *      título laranja centralizado e paginação de `<div onClick>`).
 *   2. **A lista é navegável pelo teclado.** Eram `<li onClick>`: `li` não recebe
 *      foco, não responde a Enter e não é anunciado como acionável — escolher uma
 *      marca era **impossível sem mouse**. Agora são `<button>` (via
 *      `ListaSelecao`). O mesmo valia para "Ant."/"Próx.", que eram `<div>`.
 *   3. **Falha deixou de ser muda.** O `catch` mostrava um toast e deixava a lista
 *      **vazia** — indistinguível de "nenhuma marca encontrada", que é outra
 *      coisa. Agora há `ErrorState` com "Tentar novamente" (convenção da at-56).
 *   4. **Fim do "Página 1 de 0"**: o `Math.ceil` saiu daqui e vive dentro do
 *      `Pagination`, que recebe `totalItens` + `porPagina` justamente para nenhum
 *      consumidor conseguir reintroduzir o defeito.
 *   5. **Esc, clique fora e foco** vêm do `Modal`. O clique fora existia (por
 *      `mousedown` no document, que fecha até num arrasto começado dentro do
 *      diálogo); Esc e devolução de foco, não.
 *   6. **O debounce de 400 ms** saiu daqui e vem do `SearchInput` — era a cópia
 *      manual que a at-23 introduziu, e o kit já a encapsula desde a at-56.
 *   7. **O catálogo de motocicletas elétricas também PAGINA** (pedido de Victor,
 *      09/09/2026). Antes a lista vinha inteira numa página só, e com o catálogo
 *      crescendo isso vira uma rolagem sem fim dentro do modal. ⚠️ A fatia é
 *      **local** — ver `bikeMarcasDaPagina`.
 *
 * ## O que NÃO mudou, de propósito
 *
 * ⚠️ **As props são as mesmas** (`isOpen`/`onClose`/`onSelectMarca`/`incluirBikes`,
 * em inglês). 📌 **Atualização da at-62:** `views/cotacao` foi migrada, e com ela
 * **os 9 modais de escolha ficaram sem nenhum consumidor legado** — traduzir as
 * props para português passou a ser possível. Continua **fora do escopo** de uma
 * branch de tela: é troca mecânica em 2 formulários e 9 arquivos, da frente D.
 * ⚠️ **`incluirBikes` continua atrás de um BOTÃO que troca a lista**, e não numa
 * seção fixa no topo (decisão de Victor, 06/09/2026): bike é exceção, a lista do
 * SGA é o caminho padrão. O modo também continua sendo **reposto ao abrir**, para
 * o modal nunca abrir já filtrado por causa de uma escolha anterior.
 * ⚠️ **`import './ModalMarcas.css'` foi removido e o arquivo, apagado** — mas
 * `.modal-busca-input` era definida lá e é usada por `ModalRegionais` e
 * `ModalCooperativas` (legados). Ela foi **resgatada para `ModalRegionais.css`**
 * antes da remoção; sem isso o campo de busca daqueles dois perderia borda, raio e
 * espaçamento, em silêncio.
 */

/**
 * Marca selecionável. Vem de duas fontes distintas:
 * - espelho do SGA (`codigo_marca` numérico) — o modal de modelos consulta a FIPE;
 * - catálogo de motocicletas elétricas (at-46, `bike: true`) — o modal de modelos
 *   consulta o catálogo do SAAC. O `codigo_marca` recebe o prefixo `BIKE:` só para
 *   a tela ter uma chave estável; o que decide o fluxo é `bike`/`bikeMarcaId`.
 */
export interface MarcaSelecionada {
  codigo_marca: string;
  descricao: string;
  bike?: boolean;
  bikeMarcaId?: string;
}

interface Marca {
  codigo_marca: string;
  descricao: string;
}

interface MarcaResponse {
  total: number;
  page: number;
  limit: number;
  items: Marca[];
}

interface ModalMarcasProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMarca: (marca: MarcaSelecionada) => void;
  /**
   * Oferece o botão "Motocicleta elétrica" (at-46), que TROCA a lista pelo catálogo
   * de bicicletas. Ligado nas telas de veículo da adesão; desligado onde bike não
   * faz sentido (ex.: fluxos que exigem FIPE de verdade).
   */
  incluirBikes?: boolean;
}

type Estado = 'carregando' | 'ok' | 'erro';

const POR_PAGINA = 10;

const ModalMarcas: React.FC<ModalMarcasProps> = ({
  isOpen,
  onClose,
  onSelectMarca,
  incluirBikes = false,
}) => {
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [total, setTotal] = useState(0);
  const [bikeMarcas, setBikeMarcas] = useState<BikeMarca[]>([]);
  const [modoBike, setModoBike] = useState(false);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');

  /*
   * Reposição ao abrir — inclusive o MODO, senão o modal reabriria filtrado em
   * bike por causa de uma escolha anterior.
   *
   * ⚠️ O `SearchInput` não precisa ser reposto à mão: o `Modal` desmonta os filhos
   * quando fecha, então o campo volta vazio sozinho.
   */
  useEffect(() => {
    if (!isOpen) return;
    setBusca('');
    setPagina(1);
    setModoBike(false);
  }, [isOpen]);

  /*
   * Uma fonte por vez: o botão troca a lista inteira, então não há motivo para
   * consultar as duas. São duas funções (e dois efeitos) porque **as paginações
   * são de naturezas diferentes** — ver `bikeMarcasDaPagina`.
   */
  const buscarNoSga = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = (await fetchSgaMarcas(pagina, POR_PAGINA, busca)) as MarcaResponse;

      // Resposta sem o array é resposta inesperada, não lista vazia — tratar as
      // duas como "nenhuma marca" esconderia um problema de contrato.
      if (!resposta || !Array.isArray(resposta.items)) {
        setErro('A resposta do servidor não trouxe a lista de marcas.');
        setEstado('erro');
        return;
      }

      setMarcas(resposta.items);
      setTotal(resposta.total ?? 0);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as marcas.'));
      setEstado('erro');
    }
  }, [pagina, busca]);

  const buscarNoCatalogo = useCallback(async () => {
    setEstado('carregando');
    try {
      setBikeMarcas(await listarBikeMarcas({ busca: busca || undefined }));
      setEstado('ok');
    } catch (falha) {
      setErro(
        extrairMensagemErro(falha, 'Não foi possível carregar as marcas de motocicleta elétrica.'),
      );
      setEstado('erro');
    }
  }, [busca]);

  /*
   * ⚠️ Dois efeitos, cada um com as SUAS dependências — e não um só que decide por
   * dentro. `buscarNoSga` depende da página; `buscarNoCatalogo`, **não**. Num
   * efeito único, trocar de página no modo bike refaria a requisição do catálogo e
   * piscaria o `Skeleton` no lugar de uma paginação que é puramente local.
   */
  useEffect(() => {
    if (!isOpen || modoBike) return;
    void buscarNoSga();
  }, [isOpen, modoBike, buscarNoSga]);

  useEffect(() => {
    if (!isOpen || !modoBike) return;
    void buscarNoCatalogo();
  }, [isOpen, modoBike, buscarNoCatalogo]);

  const tentarNovamente = () => (modoBike ? void buscarNoCatalogo() : void buscarNoSga());

  /*
   * A paginação do catálogo é **local**: `GET /bike-eletrica/marcas` devolve a
   * lista inteira, sem `page`/`limit` (são dezenas de marcas, e chegam numa única
   * requisição). Fatiar aqui dá a mesma experiência da lista do SGA sem pedir
   * mudança de backend.
   */
  const bikeMarcasDaPagina = bikeMarcas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const escolher = (marca: MarcaSelecionada) => {
    onSelectMarca(marca);
    onClose();
  };

  const alternarModo = () => {
    setModoBike((antes) => !antes);
    setPagina(1);
    setBusca('');
  };

  /** O vazio distingue "não há nada" de "a busca não achou nada" (spec 02 §2.7). */
  const vazio = busca ? (
    <EmptyState
      icone="busca"
      titulo="Nenhuma marca encontrada para a busca"
      descricao={`Nada corresponde a "${busca}". Tente escrever menos letras.`}
    />
  ) : (
    <EmptyState
      icone="sem-dados"
      titulo={
        modoBike ? 'Nenhuma marca de motocicleta elétrica cadastrada' : 'Nenhuma marca encontrada'
      }
      descricao={
        modoBike
          ? 'O catálogo de motocicletas elétricas é mantido pelo TI em Administração › Motocicletas Elétricas.'
          : 'O espelho de marcas do SGA parece vazio. Fale com o TI.'
      }
    />
  );

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Marca"
      descricao={
        modoBike
          ? 'Catálogo de motocicletas elétricas do SAAC.'
          : 'Define quais modelos a próxima etapa vai oferecer.'
      }
      tamanho="sm"
      aoFechar={onClose}
      rodape={
        /* Paginado nos DOIS modos (pedido de Victor, 09/09/2026) — no catálogo a
           contagem vem do tamanho da lista, porque a fatia é local. */
        estado === 'ok' && (modoBike ? bikeMarcas.length : marcas.length) > 0 ? (
          <Pagination
            pagina={pagina}
            totalItens={modoBike ? bikeMarcas.length : total}
            porPagina={POR_PAGINA}
            aoTrocar={(proxima) => {
              setPagina(proxima);
              /* No catálogo a paginação é LOCAL (ver `bikeMarcasDaPagina`), então
                 ali o `carregando` piscaria o Skeleton sem nenhuma ida à rede. */
              if (!modoBike) setEstado('carregando');
            }}
          />
        ) : undefined
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <SearchInput
            /* A chave força um campo novo ao trocar de fonte — senão o texto da
               busca anterior ficaria na tela sem estar mais sendo aplicado. */
            key={modoBike ? 'bike' : 'sga'}
            aoBuscar={(termo) => {
              /*
               * ⚠️ O `carregando` entra AQUI, no mesmo lote de `setBusca` — e não
               * apenas no efeito, que só roda um render DEPOIS. Sem isso existe um
               * render intermediário com o termo NOVO e a lista VELHA, em que a tela
               * afirma "Nada corresponde a X" antes de ter perguntado ao servidor.
               */
              setBusca(termo);
              setPagina(1);
              setEstado('carregando');
            }}
            placeholder={modoBike ? 'Buscar marca elétrica...' : 'Buscar marca...'}
            rotuloAcessivel="Buscar marca"
            className="tw-min-w-0 tw-flex-1 tw-basis-64"
          />

          {incluirBikes && (
            <Botao
              variante={modoBike ? 'primario' : 'secundario'}
              aria-pressed={modoBike}
              onClick={alternarModo}
              title={
                modoBike
                  ? 'Voltar para as marcas de veículo'
                  : 'Mostrar apenas marcas de motocicleta elétrica'
              }
            >
              {modoBike ? 'Veículos' : 'Motocicleta elétrica'}
            </Botao>
          )}
        </div>

        {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-11" />}

        {estado === 'erro' && (
          <ErrorState mensagem={erro} aoTentarNovamente={tentarNovamente} />
        )}

        {estado === 'ok' &&
          (modoBike ? (
            <ListaSelecao
              itens={bikeMarcasDaPagina}
              chave={(m) => `bike-${m.id}`}
              rotulo={(m) => m.nome}
              detalhe={(m) => `${m.totalModelos} modelo(s)`}
              aoEscolher={(m) =>
                escolher({
                  codigo_marca: `BIKE:${m.id}`,
                  descricao: m.nome,
                  bike: true,
                  bikeMarcaId: m.id,
                })
              }
              rotuloAcessivel="Marcas de motocicleta elétrica"
              vazio={vazio}
            />
          ) : (
            <ListaSelecao
              itens={marcas}
              chave={(m) => m.codigo_marca}
              rotulo={(m) => m.descricao}
              aoEscolher={escolher}
              rotuloAcessivel="Marcas de veículo"
              vazio={vazio}
            />
          ))}
      </div>
    </Modal>
  );
};

export default ModalMarcas;
